import {createAction} from 'redux-actions'
import {timestampFromArray} from '@rambler-id/input'
import {getRamblerIdHelper} from '@rambler-id/helper'
import {sentryReact as Sentry} from '@rambler-id/sentry'
import {getEmailOrderExtra} from '@rambler-id/url'
import {
  CHANGE_PROFILE,
  DETACH_EXTERNAL_ACCOUNT,
  X_REMOVE_CONTACT_INFO,
  X_SET_DEFAULT_EXTERNAL_ACCOUNT,
  SEND_EMAIL_VALIDATION,
  X_ACTIVATE_EXTERNAL_EMAIL_ACCOUNT,
  SEND_EMAIL_VALIDATION_V2,
  X_ADD_CONFIRMED_ADDITIONAL_EMAIL
} from '@rambler-id/transport'
import {apiV3} from 'utils/transport'
import {
  selectUnconfirmedEmail,
  selectSessionCredentials
} from 'models/user/selectors'
import {throwError, showSnackbar} from 'containers/App/actions'
import {
  loadUser,
  updateProfile,
  updateDisplayName,
  cancelContact,
  cancelSocialProfile,
  updateDefaultChampionat,
  updateAccounts,
  getUserAccountsInfo
} from 'models/user/actions'

export const setEditable = createAction('SET_EDITABLE')
export const throwInlineError = createAction('THROW_INLINE_ERROR')
export const emailSent = createAction('EMAIL_SENT')

const PROFILE_EDITING_IS_NOT_ALLOWED =
  'В данный момент редактирование профиля недоступно. Подождите немного и попробуйте снова'

export const changeProfile = (data) => async (dispatch) => {
  let profile = data

  if ('firstname' in data) {
    const {firstname = '', lastname = ''} = data

    profile = {
      firstname: firstname.trim(),
      lastname: lastname.trim()
    }
  } else if ('geoid' in data) {
    profile = {
      geoid: data.geoid || 0
    }
  } else if ('birthday' in data) {
    profile = {
      birthday: timestampFromArray(data.birthday)
    }
  }

  try {
    await apiV3(`/legacy/${CHANGE_PROFILE}`, profile)
    dispatch(setEditable(null))
    dispatch(updateProfile(profile))

    if ('firstname' in profile) {
      dispatch(
        updateDisplayName(
          `${profile.firstname || ''} ${profile.lastname || ''}`.trim()
        )
      )

      // emit login to reload topline
      const ramblerIdHelper = await getRamblerIdHelper()

      ramblerIdHelper.emit('login')
    }

    dispatch(showSnackbar('Данные сохранены'))
  } catch (error) {
    if (error.code === -17) {
      dispatch(throwInlineError(PROFILE_EDITING_IS_NOT_ALLOWED))

      return
    }

    Sentry.captureException(error)
    dispatch(throwError('При сохранении данных произошла ошибка', error))
  }
}

export const attachSocial =
  ({action, provider, error}) =>
  (dispatch) => {
    if (action !== 'attach') {
      return
    }

    if (error === -17) {
      dispatch(
        throwError(
          `В данный момент невозможно привязать ${
            provider === 'webauthn' ? 'электронный ключ' : 'социальную сеть'
          } к профилю. Подождите немного и попробуйте снова`
        )
      )

      return
    }

    if (error === -3) {
      dispatch(
        throwError(
          `Что-то пошло не так. Попробуйте привязать ${
            provider === 'webauthn' ? 'электронный ключ' : 'социальную сеть'
          } еще раз`
        )
      )

      return
    }

    if (error === -5) {
      dispatch(
        throwError(
          provider === 'webauthn'
            ? 'Привязать электронный ключ невозможно, так как он уже привязан к другому профилю. Если вы этого не делали, обратитесь в <a href="https://help.rambler.ru/feedback/id/">службу поддержки</a>'
            : 'Привязать социальную сеть невозможно, так как она уже привязана к другому профилю. Если вы этого не делали, обратитесь в <a href="https://help.rambler.ru/feedback/id/">службу поддержки</a>'
        )
      )

      return
    }

    if (error) {
      dispatch(
        throwError(
          `Привязка ${
            provider === 'webauthn' ? 'электронного ключа' : 'социальной сети'
          } не удалась`
        )
      )

      return
    }

    dispatch(loadUser())
    dispatch(
      showSnackbar(
        provider === 'webauthn'
          ? 'Ключ успешно привязан'
          : 'Социальная сеть успешно привязана'
      )
    )
  }

export const detachSocial = (id, provider) => async (dispatch) => {
  try {
    await apiV3(`/legacy/${DETACH_EXTERNAL_ACCOUNT}`, {id, provider})
    dispatch(cancelSocialProfile({id, provider}))
    dispatch(showSnackbar('Социальная сеть отвязана'))
  } catch (error) {
    if (error.code === -17) {
      dispatch(throwError(PROFILE_EDITING_IS_NOT_ALLOWED, error))

      return
    }

    Sentry.captureException(error)
    dispatch(throwError('Не удалось отвязать социальную сеть', error))
  }
}

export const deleteContact = (contact) => async (dispatch) => {
  try {
    await apiV3(`/legacy/${X_REMOVE_CONTACT_INFO}`, {contact})
    dispatch(cancelContact(contact))
    dispatch(
      showSnackbar(
        `${/@/.test(contact) ? 'Почтовый ящик' : 'Номер телефона'} удален`
      )
    )
  } catch (error) {
    if (error.code === -17) {
      dispatch(throwError(PROFILE_EDITING_IS_NOT_ALLOWED, error))

      return
    }

    Sentry.captureException(error)
    dispatch(
      throwError(
        'Не удалось удалить ' +
          (/@/.test(contact) ? 'почтовый ящик' : 'номер телефона'),
        error
      )
    )
  }
}

export const setDefaultAccount = (accountId) => async (dispatch) => {
  try {
    await apiV3(`/legacy/${X_SET_DEFAULT_EXTERNAL_ACCOUNT}`, {
      accountId,
      provider: 'championat'
    })
    dispatch(updateDefaultChampionat(accountId))
    dispatch(showSnackbar(`Профиль ${accountId} выбран основным`))
  } catch (error) {
    if (error.code === -17) {
      dispatch(throwError(PROFILE_EDITING_IS_NOT_ALLOWED, error))

      return
    }

    Sentry.captureException(error)
    dispatch(
      throwError(`Не удалось сделать профиль ${accountId} основным`, error)
    )
  }
}

export const resendEmail = (email) => async (dispatch, getState) => {
  const emailAccount = selectUnconfirmedEmail(getState())

  try {
    await apiV3(`/legacy/${SEND_EMAIL_VALIDATION}`, {
      email,
      method: X_ACTIVATE_EXTERNAL_EMAIL_ACCOUNT,
      template: 'x-mail-activate-2',
      extra: getEmailOrderExtra(
        {
          password: emailAccount.passwordType !== 'NONE',
          email
        },
        window.location.href
      )
    })

    dispatch(showSnackbar('Письмо отправлено'))
  } catch (error) {
    if (/rate limit exceed/.test(error.message)) {
      dispatch(throwError('Попробуйте еще раз немного позже', error))

      return
    }

    Sentry.captureException(error)
    dispatch(throwError('Не удалось отправить письмо для подтверждения', error))
  }
}

export const resendEmailOrClosePopup =
  (onResolve) => async (dispatch, getState) => {
    const {accountId: unconfirmedAccount} = selectUnconfirmedEmail(getState())
    const {account: currentAccount} = selectSessionCredentials(getState())
    const {profile} = await getUserAccountsInfo()(dispatch)

    if (!profile) {
      dispatch(resendEmail(unconfirmedAccount))
    } else {
      const {info = {}} = profile
      const {rawAccounts = {}} = info
      const {accounts = []} = rawAccounts

      if (
        accounts.some(
          ({accountId, accountType}) =>
            accountId === currentAccount && accountType === 'UNCONFIRMED_MAIL'
        )
      ) {
        dispatch(resendEmail(unconfirmedAccount))
      } else {
        dispatch(updateAccounts(accounts))

        dispatch(showSnackbar('Профиль уже подтвержден'))

        onResolve()
      }
    }
  }

export const resendEmailForContact =
  (contact, recaptcha = '') =>
  async (dispatch) => {
    try {
      const [orderId, orderValue] = recaptcha.split(/@/)

      await apiV3(`/legacy/${SEND_EMAIL_VALIDATION_V2}`, {
        email: contact,
        method: X_ADD_CONFIRMED_ADDITIONAL_EMAIL,
        template: 'mail-confirm-id',
        extra: JSON.stringify({email: contact, source: 'contact'}),
        __rpcOrderId: orderId,
        __rpcOrderValue: orderValue
      })

      dispatch(emailSent(contact))
      dispatch(showSnackbar('Письмо отправлено'))
    } catch (error) {
      if (/rate limit exceed/.test(error.message)) {
        dispatch(throwError('Попробуйте еще раз немного позже', error))

        return
      }

      Sentry.captureException(error)
      dispatch(
        throwError('Не удалось отправить письмо для подтверждения', error)
      )
    }
  }
